/** @file Encodes a 0/1 coverage mask as a 1-bit palette PNG (white, tRNS 0/255) with stored deflate. */
import { invariant } from '@pose2d/std/invariant';

const SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];
/** Largest payload of one stored deflate block (RFC 1951 §3.2.4). */
const STORED_MAX = 65535;

/** Bitwise CRC-32 (reflected polynomial 0xedb88320) of all bytes; the page is a few KiB, so no table. */
function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (const byte of bytes) {
    c ^= byte;
    for (let k = 0; k < 8; k++) c = (c >>> 1) ^ (0xedb88320 & -(c & 1));
  }
  return (c ^ 0xffffffff) >>> 0;
}

/** Big-endian bytes of an unsigned 32-bit integer. */
const u32 = (n: number): number[] => [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255];

/** Adler-32 (RFC 1950) of all bytes. */
function adler32(bytes: Uint8Array): number {
  let a = 1, b = 0;
  for (const byte of bytes) {
    a = (a + byte) % 65521;
    b = (b + a) % 65521;
  }
  return ((b << 16) | a) >>> 0;
}

/** One chunk: length, then type + data, then the CRC of type + data. */
function chunk(type: string, data: Uint8Array): number[] {
  const body = Uint8Array.from([...Array.from(type, (c) => c.charCodeAt(0)), ...data]);
  return [...u32(data.length), ...body, ...u32(crc32(body))];
}

/** zlib stream (header 0x78 0x01) holding `raw` in stored (uncompressed) deflate blocks, then Adler-32. */
function zlibStored(raw: Uint8Array): Uint8Array {
  const blocks = Math.max(1, Math.ceil(raw.length / STORED_MAX));
  const out = new Uint8Array(2 + blocks * 5 + raw.length + 4);
  const view = new DataView(out.buffer);
  out[0] = 0x78;
  out[1] = 0x01;
  let o = 2;
  for (let b = 0; b < blocks; b++) {
    const start = b * STORED_MAX, len = Math.min(STORED_MAX, raw.length - start);
    out[o] = b === blocks - 1 ? 1 : 0;
    view.setUint16(o + 1, len, true);
    view.setUint16(o + 3, len ^ 0xffff, true);
    out.set(raw.subarray(start, start + len), o + 5);
    o += 5 + len;
  }
  view.setUint32(o, adler32(raw));
  return out;
}

/** Scanlines: filter byte 0, then 8 pixels per byte, most significant bit first. */
function packRows(mask: Uint8Array, width: number, height: number): Uint8Array {
  const stride = Math.ceil(width / 8) + 1;
  const raw = new Uint8Array(stride * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * stride + 1 + (x >> 3);
      if ((mask[y * width + x] ?? 0) !== 0) raw[i] = (raw[i] ?? 0) | (0x80 >> (x & 7));
    }
  }
  return raw;
}

/** PNG bytes of `mask` (one byte per pixel, row-major; non-zero = opaque white, zero = fully transparent). */
export function encodeGlyphPage(mask: Uint8Array, width: number, height: number): Uint8Array {
  invariant(Number.isInteger(width) && Number.isInteger(height) && width > 0 && height > 0, 'page size must be positive integers');
  invariant(mask.length === width * height, 'mask must hold width * height bytes');
  const ihdr = new Uint8Array(13);
  const view = new DataView(ihdr.buffer);
  view.setUint32(0, width);
  view.setUint32(4, height);
  ihdr.set([1, 3, 0, 0, 0], 8); // bit depth 1, colour type 3 (palette), deflate, filter 0, no interlace
  return Uint8Array.from([
    ...SIGNATURE,
    ...chunk('IHDR', ihdr),
    ...chunk('PLTE', Uint8Array.of(255, 255, 255, 255, 255, 255)),
    ...chunk('tRNS', Uint8Array.of(0, 255)),
    ...chunk('IDAT', zlibStored(packRows(mask, width, height))),
    ...chunk('IEND', new Uint8Array(0)),
  ]);
}
